import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, FileDown, FileText } from "lucide-react";
import api from "@/lib/api";
import { lcsDiff, categorizeMistakes, summarize, CATEGORY_LABEL, explain } from "@/lib/reportDiff";
import { downloadPagePdf, downloadPageCsv } from "@/lib/reportPdf";

// -------------------------------------------------------------------------
// Presentation-only helpers. Stored accuracy/WPM come from backend as-is.
// -------------------------------------------------------------------------

const CAT_STYLE = {
  MISSING:         "bg-rose-50 text-rose-700 border-rose-200",
  MISSING_SEGMENT: "bg-rose-50 text-rose-700 border-rose-200",
  MISSING_SPACE:   "bg-rose-50 text-rose-700 border-rose-200",
  EXTRA:           "bg-emerald-50 text-emerald-700 border-emerald-200",
  EXTRA_SEGMENT:   "bg-emerald-50 text-emerald-700 border-emerald-200",
  EXTRA_SPACE:     "bg-emerald-50 text-emerald-700 border-emerald-200",
  WRONG:           "bg-amber-50 text-amber-800 border-amber-200",
  CASE:            "bg-amber-50 text-amber-800 border-amber-200",
  SYMBOL:          "bg-amber-50 text-amber-800 border-amber-200",
};

function showChar(c) {
  if (c === null || c === undefined) return <span className="text-zinc-400">—</span>;
  if (c === " ") return <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">[space]</span>;
  return <span className="font-mono">{c}</span>;
}

function CompareBlock({ ops }) {
  // Colored inline diff with tooltips — one line, wraps naturally.
  return (
    <div className="font-mono text-sm leading-7 whitespace-pre-wrap break-words">
      {ops.map((op, i) => {
        if (op.type === "eq") {
          return <span key={i} className="text-zinc-800">{op.char}</span>;
        }
        if (op.type === "del") {
          return (
            <span
              key={i}
              title={`Missing: "${op.char === " " ? "[SPACE]" : op.char}" at position ${op.oIndex + 1}`}
              className="bg-rose-100 text-rose-800 rounded px-0.5 line-through decoration-rose-500"
            >{op.char === " " ? "␣" : op.char}</span>
          );
        }
        return (
          <span
            key={i}
            title={`Extra typed: "${op.char === " " ? "[SPACE]" : op.char}"`}
            className="bg-emerald-100 text-emerald-800 rounded px-0.5"
          >{op.char === " " ? "␣" : op.char}</span>
        );
      })}
    </div>
  );
}

function SummaryChip({ label, value, tone = "neutral" }) {
  const tones = {
    neutral: "bg-zinc-50 text-zinc-700 border-[var(--line)]",
    red:     "bg-rose-50 text-rose-700 border-rose-200",
    green:   "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber:   "bg-amber-50 text-amber-800 border-amber-200",
  };
  return (
    <div className={`rounded-lg border px-3 py-2 ${tones[tone]}`}>
      <div className="text-[10px] uppercase tracking-widest opacity-70">{label}</div>
      <div className="text-lg font-bold leading-none mt-1">{value}</div>
    </div>
  );
}

function FieldReport({ field }) {
  const ops = useMemo(() => lcsDiff(field.original || "", field.typed || ""), [field.original, field.typed]);
  const mistakes = useMemo(() => categorizeMistakes(ops), [ops]);
  const s = useMemo(() => summarize(mistakes), [mistakes]);

  return (
    <div className="card-clean p-5" data-testid={`field-report-${field.index}`}>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="font-display text-lg font-bold">Field {field.index + 1}</div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] uppercase tracking-widest text-zinc-500">Existing Accuracy</span>
          <span className={`chip ${field.accuracy >= 95 ? "text-green-700 border-green-200" : field.accuracy >= 80 ? "text-amber-700 border-amber-200" : "text-red-700 border-red-200"}`}>{field.accuracy}%</span>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4 text-sm">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">Original Text</div>
          <div className="font-mono leading-7 whitespace-pre-wrap break-words bg-zinc-50 rounded-lg p-3 border border-[var(--line)]">{field.original || "—"}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">Candidate Typed</div>
          <div className="font-mono leading-7 whitespace-pre-wrap break-words bg-zinc-50 rounded-lg p-3 border border-[var(--line)]">{field.typed || "—"}</div>
        </div>
      </div>

      <div className="mt-4">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5 flex items-center gap-3">
          <span>Clear Comparison</span>
          <span className="inline-flex items-center gap-1 text-[10px] normal-case tracking-normal">
            <span className="inline-block w-3 h-3 rounded bg-rose-100 border border-rose-200" /> missing
            <span className="inline-block w-3 h-3 rounded bg-emerald-100 border border-emerald-200 ml-2" /> extra
            <span className="ml-2 text-zinc-400">(hover for details)</span>
          </span>
        </div>
        <div className="bg-zinc-50 rounded-lg p-3 border border-[var(--line)]">
          <CompareBlock ops={ops} />
        </div>
      </div>

      <div className="mt-4">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">Mistake Summary</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
          <SummaryChip label="Total" value={s.total} tone={s.total ? "amber" : "green"} />
          <SummaryChip label="Missing" value={s.missing} tone="red" />
          <SummaryChip label="Extra" value={s.extra} tone="green" />
          <SummaryChip label="Wrong / Repl" value={s.wrong} tone="amber" />
          <SummaryChip label="Miss Space" value={s.missing_space} tone="red" />
          <SummaryChip label="Extra Space" value={s.extra_space} tone="green" />
          <SummaryChip label="Case" value={s.case} tone="amber" />
          <SummaryChip label="Symbol" value={s.symbol} tone="amber" />
        </div>
      </div>

      <div className="mt-4">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">Detailed Mistakes</div>
        {mistakes.length === 0 ? (
          <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
            No mistakes detected in this field.
          </div>
        ) : (
          <div className="rounded-lg border border-[var(--line)] overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50">
                <tr>
                  {["#", "Type", "Position", "Expected", "Typed", "Explanation"].map((h) => (
                    <th key={h} className="text-left px-3 py-2 text-[11px] uppercase tracking-widest text-zinc-500 font-semibold whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {mistakes.map((m, i) => (
                  <tr key={i} className="border-t border-[var(--line)]">
                    <td className="px-3 py-2 text-zinc-500">{i + 1}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-semibold ${CAT_STYLE[m.category] || "bg-zinc-50 border-[var(--line)]"}`}>
                        {CATEGORY_LABEL[m.category] || m.category}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-zinc-600">{m.position}</td>
                    <td className="px-3 py-2">{showChar(m.expected)}</td>
                    <td className="px-3 py-2">{showChar(m.typed)}</td>
                    <td className="px-3 py-2 text-zinc-700">{explain(m)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminSubmissionDetail() {
  const { sid } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [emp, setEmp] = useState(null);
  useEffect(() => {
    (async () => {
      const r = await api.get(`/admin/submissions/${sid}`);
      setData(r.data);
      // Best-effort: pull the employee for name/email on the PDF cover.
      try {
        const eid = r.data?.submission?.employee_id;
        if (eid) {
          const emps = await api.get("/admin/employees");
          const list = emps.data?.items || emps.data || [];
          const found = (Array.isArray(list) ? list : []).find((e) => e.id === eid);
          if (found) setEmp(found);
        }
      } catch (e) { /* non-fatal */ }
    })();
  }, [sid]);
  if (!data) return <div className="text-zinc-500">Loading…</div>;

  const onDownloadPdf = () => downloadPagePdf({
    pageName: data.page_name || "Page",
    fields: data.fields,
    submission: data.submission,
    employee: emp,
  });
  const onDownloadCsv = () => downloadPageCsv({
    pageName: data.page_name || "Page",
    fields: data.fields,
    submission: data.submission,
    employee: emp,
  });

  return (
    <div>
      <button onClick={() => nav(-1)} className="text-sm text-zinc-500 inline-flex items-center gap-1 mb-4"><ArrowLeft size={14} /> Back</button>
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-4xl font-bold tracking-tight">{data.page_name} — Result Detail</h1>
          <p className="text-zinc-500 mt-2 text-sm">Clear per-field explanation of every candidate mistake. Existing accuracy and WPM are shown as stored — nothing recomputed.</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="download-page-pdf" onClick={onDownloadPdf} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--ink)] text-white text-sm font-semibold hover:bg-[var(--accent)]">
            <FileDown size={14} /> Download This Page (PDF)
          </button>
          <button data-testid="download-page-csv" onClick={onDownloadCsv} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[var(--line)] bg-white text-sm font-semibold hover:bg-zinc-50">
            <FileText size={14} /> CSV
          </button>
        </div>
      </div>

      <div className="mt-6 space-y-4">
        {data.fields.map((f) => <FieldReport key={f.index} field={f} />)}
      </div>
    </div>
  );
}
